import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Architecture Firm Content) ---
import {
    // Core Concepts & General
    faDraftingCompass,  // Architecture/Design
    faBuilding,         // Building/Infrastructure
    faSitemap,          // Master Planning
    faPalette,          // Interior Design
    faRulerCombined,    // Structural Design
    faCogs,             // MEP Design / Engineering
    faFireExtinguisher, // Fire Safety
    faTree,             // Landscape Design (Alternative: faSeedling)
    faHeartPulse,       // Patient-Centered Care
    faCheckCircle,      // Compliance/Quality
    faMapMarkedAlt,     // Site Selection/Feasibility
    faDollarSign,       // Budget/Financial Planning
    faLeaf,             // Sustainability/Green Building
    faUsers,            // Team/Collaboration
    faLightbulb,        // Vision/Concept/Phase I
    // faMapMarkedAlt used again for Phase II
    faProjectDiagram,   // Interdepartmental/Flow/Phase III
    faHardHat,          // Construction/Phase IV (Alternative: faTools)
    faUserTie,          // Consultants/Doctors
    faUserCog,          // Engineers
    faUserShield,       // Construction Managers
    faGlobeAsia,        // Geography/Regions
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faTasks, faIndustry, faAward, faHospital, faUserMd, faUniversity, faDiagnoses,
    faNetworkWired, faTools
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Comprehensive Services
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Considerations
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Multidisciplinary Team
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Design Process
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Serving Nationwide

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for architecture
    const heroBgImage = 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Modern architecture image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Best Medical Architecture Firm in India</h1>
            <p className="cliniqor-sub-heading">
              Leading the Future of Healthcare Design: Designing Healthcare Infrastructure for the Future
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor specializes in comprehensive, integrated healthcare solutions, from concept to commissioning. We transform visions into state-of-the-art facilities meeting regulatory, operational, and design standards for excellent patient care and maximum efficiency.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION (Optional Elaboration) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Holistic, Patient-Centric Design</h3>
                     <p>Whether it's a multi-specialty hospital, super-specialty care facility, or healthcare campus, Cliniqor delivers designs meeting current and future needs. Our expertise spans planning, architecture, interiors, structural, MEP, and landscape design for a seamless approach.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- COMPREHENSIVE SERVICES (Features Section - adapted for 7 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Comprehensive Healthcare Design Services</h2>
              <p>Improving patient care, operational efficiency, and long-term sustainability through specialized design.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Adjusted grid for potentially 7 items - requires CSS adjustment for .cliniqor-features-grid */}
            <style>{`.cliniqor-features-grid { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }`}</style> {/* Basic Responsive Grid */}
            <div className="cliniqor-features-grid">
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <h4>1. Healthcare Architecture Design</h4>
                    <p>Functional, flexible, aesthetic spaces promoting healing and enhancing outcomes.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSitemap} /></div>
                    <h4>2. Master Planning & Campus Design</h4>
                    <p>Long-term strategies for layout, growth, and adaptable infrastructure.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div>
                    <h4>3. MEP Design (Mechanical, Electrical, Plumbing)</h4>
                    <p>Advanced HVAC, energy-efficient solutions, and critical infrastructure design.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faPalette} /></div>
                    <h4>4. Interior Design Services</h4>
                    <p>Soothing, functional interiors integrating comfort and efficiency for patient experience.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faRulerCombined} /></div>
                    <h4>5. Structural Design Services</h4>
                    <p>Safe, robust, and sustainable designs considering loads, seismic resistance, and accessibility.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFireExtinguisher} /></div>
                    <h4>6. Fire Safety & Emergency Planning</h4>
                    <p>Incorporating safety protocols, exits, and evacuation pathways for patient/staff well-being.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTree} /></div>
                    <h4>7. Landscape Design</h4>
                    <p>Outdoor spaces like healing gardens promoting mental well-being and recovery.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- KEY CONSIDERATIONS (Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Key Considerations in Healthcare Infrastructure Design</h2>
               <p>Critical factors guiding our specialized planning and design phase.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Considerations */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <div className="cliniqor-content"><h4>1. Patient-Centered Care Design</h4> <p>Prioritizing easy navigation, privacy, safety, and comfort in healing spaces.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <div className="cliniqor-content"><h4>2. Regulatory Compliance & Legal Approvals</h4> <p>Ensuring adherence to latest local, state, and national codes and requirements.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <div className="cliniqor-content"><h4>3. Feasibility Studies & Site Selection</h4> <p>Evaluating location, access, conditions, and proximity for optimal community service.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <div className="cliniqor-content"><h4>4. Budget and Financial Planning</h4> <p>Aligning high-quality design with available budget without compromising standards.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLeaf} /></div>
                    <div className="cliniqor-content"><h4>5. Sustainability & Green Building Design</h4> <p>Integrating energy efficiency and eco-friendly practices for reduced footprint and costs.</p> </div>
                 </div>
                 {/* Only 5 items */}
            </div>
          </div>
        </section>

       {/* --- MULTIDISCIPLINARY TEAM (Simple Text Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>A Multidisciplinary Team for Optimal Healthcare Design</h2>
                 <p>Collaborative synergy between healthcare professionals and design specialists.</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center' }}>
                   <p>Our team consists of Healthcare Consultants & Doctors, Architects & Interior Designers, Structural Engineers, MEP Engineers, Biomedical Engineers, and Construction Managers, working together to deliver the best results.</p>
                    {/* Optional: List icons for each role */}
                   {/* <p>
                       <FontAwesomeIcon icon={faUserTie} /> Healthcare Consultants & Doctors |
                       <FontAwesomeIcon icon={faDraftingCompass} /> Architects & Designers |
                       <FontAwesomeIcon icon={faRulerCombined} /> Structural Engineers |
                       <FontAwesomeIcon icon={faUserCog} /> MEP & Biomedical Engineers |
                       <FontAwesomeIcon icon={faUserShield} /> Construction Managers
                   </p> */}
              </div>
           </div>
        </section>


        {/* --- DESIGN PROCESS (Reusing Detailed Planning Layout) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section">
          <div className="cliniqor-container">
             <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Cliniqor’s Hospital Design Process: Concept to Completion</h2>
               <p>Executing every project with precision, expertise, and collaboration.</p>
            </div>
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="cliniqor-content"><h4>Phase I: Strategic Planning & Assessment</h4> <p>Vision alignment, business/market assessment, feasibility, and initial space plan blueprint.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <div className="cliniqor-content"><h4>Phase II: Site Evaluation & Conceptual Planning</h4> <p>Site analysis (weather, soil, transport), concept design balancing aesthetics and function.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div className="cliniqor-content"><h4>Phase III: Detailed Design & Interdepartmental Planning</h4> <p>Internal flow planning, logical department relationships, critical zone designation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHardHat} /></div>
                    <div className="cliniqor-content"><h4>Phase IV: Final Design & Construction Oversight</h4> <p>Construction planning, adherence monitoring (safety, budget, timeline), evaluation.</p> </div>
                 </div>
            </div>
          </div>
        </section>


       {/* --- SERVING NATIONWIDE (Simple List Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-simple-list-section"> {/* Reusing dark background */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2 style={{color: '#fff'}}>Serving Healthcare Projects Nationwide</h2>
               </div>
              <div className="cliniqor-fade-in-up" style={{ textAlign: 'center', color: '#eee' }}>
                  {/* Using a paragraph for the list */}
                  <p style={{fontSize: '1.1em', lineHeight: '1.6' ,color:'white'}}>
                     <FontAwesomeIcon icon={faGlobeAsia} style={{ marginRight: '10px', }}/>
                     Delhi ● Bangalore ● Chennai ● Mumbai ● Ahmedabad ● Hyderabad ● Guwahati ● Pune ● Jaipur ● Lucknow ● Raipur ● Bhopal ● Kochi ● Patna ● Kolkata ● And expanding rapidly across India and internationally.
                  </p>
              </div>
           </div>
        </section>

       {/* Removed old "Why Stand Apart" section */}


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Let’s Build Your Dream Healthcare Facility Together</h3>
          <p>Ready to design a world-class healthcare facility? Cliniqor offers the expertise, innovation, and dedication to make your dream hospital a reality. Contact us today to discuss your project and create an environment promoting health, healing, and sustainable growth.</p>
          <a href="#contact" className="cliniqor-cta-button">Get in Touch with Our Experts</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;